import type { ReactNode } from "react";
import { X } from "lucide-react";

export interface DialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /** "lg" fits forms such as the product editor; the body scrolls. */
  size?: "md" | "lg";
}

/**
 * Accessible modal dialog with backdrop, title, and customizable footer.
 * Closes on backdrop click and escape key.
 */
export function Dialog({ open, title, children, onClose, footer, size = "md" }: DialogProps) {
  if (!open) return null;

  return (
    <div
      className="dialog-backdrop"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        className={size === "lg" ? "dialog dialog--lg" : "dialog"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog__header">
          <h3>{title}</h3>
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer ? <div className="dialog__footer">{footer}</div> : null}
      </div>
    </div>
  );
}
